/** Base error for all TagadaPay headless SDK errors. */
export class TagadaError extends Error {
  public readonly code: string;
  public readonly statusCode?: number;
  public readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'TagadaError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
}

/** Thrown when the API returns 401/403. */
export class TagadaAuthError extends TagadaError {
  constructor(message = 'Authentication failed. Check your API key or checkout token.') {
    super(message, 'auth_error', 401);
    this.name = 'TagadaAuthError';
  }
}

/** Thrown when the API returns 422 with validation errors. */
export class TagadaValidationError extends TagadaError {
  public readonly errors: Array<{ field: string; message: string }>;
  /**
   * Application error code from the API, when it sends one — e.g.
   * `MISSING_SHIPPING_ADDRESS` when a cart containing a shippable product is
   * paid with no address (#TAG-1989). Branch on this rather than on the
   * message, which is customer-facing and may be reworded.
   */
  public readonly appCode?: string;

  constructor(
    message: string,
    errors: Array<{ field: string; message: string }> = [],
    appCode?: string,
    details?: Record<string, unknown>,
  ) {
    super(message, 'validation_error', 422, { errors, ...details });
    this.name = 'TagadaValidationError';
    this.errors = errors;
    this.appCode = appCode;
  }
}

/** Thrown when a resource is not found (404). */
export class TagadaNotFoundError extends TagadaError {
  constructor(resource: string, id: string) {
    super(`${resource} "${id}" not found.`, 'not_found', 404);
    this.name = 'TagadaNotFoundError';
  }
}

/** Thrown when the API returns 429. */
export class TagadaRateLimitError extends TagadaError {
  public readonly retryAfterMs: number;

  constructor(retryAfterMs = 1000) {
    super(`Rate limited. Retry after ${retryAfterMs}ms.`, 'rate_limit', 429);
    this.name = 'TagadaRateLimitError';
    this.retryAfterMs = retryAfterMs;
  }
}

/** Thrown when a payment fails or requires action. */
export class TagadaPaymentError extends TagadaError {
  public readonly paymentId?: string;
  public readonly requireAction?: string;

  constructor(
    message: string,
    opts?: { paymentId?: string; requireAction?: string; details?: Record<string, unknown> },
  ) {
    super(message, 'payment_error', undefined, opts?.details);
    this.name = 'TagadaPaymentError';
    this.paymentId = opts?.paymentId;
    this.requireAction = opts?.requireAction;
  }
}

/** Thrown on network / fetch failures. */
export class TagadaNetworkError extends TagadaError {
  constructor(message = 'Network request failed. Check your connection.') {
    super(message, 'network_error');
    this.name = 'TagadaNetworkError';
  }
}
